import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bell, Settings, ShieldCheck } from 'lucide-react';
import TopTabs from '../controls/TopTabs';
import SearchInput from '../controls/SearchInput';
import { surfaceClasses } from '../tokens';
import { commandSidebarItems } from '../../ui-foundation/commandMenu';

const cx = (...v) => v.filter(Boolean).join(' ');

function SidebarNav({ items = [], activePathname = '', activeSearch = '' }) {
  const groupedItems = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const section = String(item?.section || 'Menu');
      if (!map.has(section)) map.set(section, []);
      map.get(section).push(item);
    });
    return Array.from(map.entries());
  }, [items]);

  const isItemActive = (to) => {
    const raw = String(to || '');
    const pureTo = raw.split('?')[0];
    const targetParams = new URLSearchParams(raw.includes('?') ? raw.split('?')[1] : '');
    const currentParams = new URLSearchParams(String(activeSearch || ''));
    if (!pureTo) return false;
    if (pureTo === '/dadu') return activePathname === '/dadu' || activePathname === '/command-center';
    if ([...targetParams.keys()].length > 0) {
      if (activePathname !== pureTo) return false;
      return [...targetParams.entries()].every(([k, v]) => String(currentParams.get(k) || '') === String(v || ''));
    }
    return activePathname === pureTo;
  };

  return (
    <aside className="hidden w-[248px] shrink-0 xl:block">
      <div className={`${surfaceClasses.panel} sticky top-4 h-[calc(100vh-2rem)] p-4`}>
        <div className="mb-6">
          <p className="text-xl font-bold tracking-tight text-[var(--cc-accent)]">COMMAND CENTER</p>
          <p className="cc-text-subtle mt-1 text-[11px] uppercase tracking-[0.16em]">ISP Operations</p>
        </div>
        <nav className="h-[calc(100%-9.5rem)] space-y-4 overflow-y-auto pr-1">
          {groupedItems.map(([section, sectionItems]) => (
            <div key={section}>
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#62768f]">{section}</p>
              <div className="space-y-1.5">
                {sectionItems.map((item) => {
                  const active = isItemActive(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cx(
                        'flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm transition',
                        active ? 'bg-[#11311f] text-[#8ff8bc]' : 'cc-text-secondary hover:bg-[#151f2f] hover:text-[var(--cc-text)]',
                      )}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="mt-6 rounded-xl bg-[var(--cc-surface-2)] p-3">
          <p className="cc-text-muted text-[11px]">Autonomous sync aktif</p>
          <p className="cc-text-primary mt-1 text-sm font-semibold">Mode produksi</p>
        </div>
      </div>
    </aside>
  );
}

function Topbar({ searchValue = '', onSearchChange, tabs, activeTab, onTabChange }) {
  return (
    <div className={`${surfaceClasses.panel} mb-4 flex h-14 items-center justify-between gap-3 px-4`}>
      <TopTabs tabs={tabs} active={activeTab} onChange={onTabChange} />
      <SearchInput value={searchValue} onChange={onSearchChange} />
      <div className="hidden items-center gap-2 md:flex">
        <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#111a29] text-[#9cb2ca]"><Bell className="h-4 w-4" /></button>
        <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#111a29] text-[#9cb2ca]"><Settings className="h-4 w-4" /></button>
        <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#111a29] text-[var(--cc-accent)]"><ShieldCheck className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function AppShell({
  sidebarItems = commandSidebarItems,
  tabs = [
    { value: 'overview', label: 'Overview' },
    { value: 'analytics', label: 'Analytics' },
    { value: 'logs', label: 'Logs' },
  ],
  initialTab = 'overview',
  searchValue = '',
  onSearchChange,
  children,
}) {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [localSearch, setLocalSearch] = useState('');

  return (
    <div className={surfaceClasses.shell}>
      <div className="mx-auto flex w-full max-w-[1760px] gap-4 p-4 xl:p-6">
        <SidebarNav items={sidebarItems} activePathname={location.pathname} activeSearch={location.search} />
        <div className="min-w-0 flex-1">
          <Topbar
            searchValue={searchValue || localSearch}
            onSearchChange={onSearchChange || ((event) => setLocalSearch(event.target.value))}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          {children}
        </div>
      </div>
    </div>
  );
}
